import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Account } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { NATUREZAS_PLANO_CONTAS } from "../../lib/options";
import { ACCOUNT_TYPES } from "../../constants/accountTypes";
import { buildQuery } from "../../lib/paged";

export const AccountsPage: React.FC = () => {
  const { client } = useAuth();
  const [opexOptions, setOpexOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [natureFilter, setNatureFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [exporting, setExporting] = useState(false);

  const listFilters = useMemo(
    () => ({
      ...(natureFilter ? { nature: natureFilter } : {}),
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(activeFilter !== "ALL" ? { isActive: activeFilter === "true" } : {}),
    }),
    [natureFilter, typeFilter, activeFilter]
  );

  useEffect(() => {
    const loadOpexCategories = async () => {
      const response = await client.get("/modules/opex-categories?take=200&skip=0");
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string }>;
      };
      setOpexOptions(payload.data.map((item) => ({ id: item.id, label: item.name })));
    };
    void loadOpexCategories();
  }, [client]);

  const handleExport = useCallback(
    async (query: Record<string, unknown>) => {
      if (exporting) {
        return;
      }
      setExporting(true);
      try {
        const queryString = buildQuery(query);
        const response = await client.get(`/modules/accounts/export?${queryString}`);
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(text || "Falha ao exportar.");
        }
        const blob = await response.blob();
        const header = response.headers.get("Content-Disposition") ?? "";
        const match = header.match(/filename="([^"]+)"/i);
        const filename = match?.[1] ?? "plano_de_contas.xlsx";
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        alert(error instanceof Error ? error.message : "Falha ao exportar.");
      } finally {
        setExporting(false);
      }
    },
    [client, exporting]
  );

  return (
    <CrudPage<Account>
      title="Plano de contas contabeis"
      endpoint="/modules/accounts"
      searchPlaceholder="Buscar por codigo ou nome..."
      listFilters={listFilters}
      renderFilters={
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label mb-1">Natureza</label>
            <select
              className="form-select"
              value={natureFilter}
              onChange={(event) => setNatureFilter(event.target.value)}
            >
              <option value="">Todas</option>
              {NATUREZAS_PLANO_CONTAS.map((nature) => (
                <option key={nature.value} value={nature.value}>
                  {nature.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label mb-1">Tipo</label>
            <select
              className="form-select"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="">Todos</option>
              {ACCOUNT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label mb-1">Ativo</label>
            <select
              className="form-select"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              <option value="ALL">Todos</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>
        </div>
      }
      columns={[
        { label: "Codigo", render: (item) => item.code },
        { label: "Conta", render: (item) => item.name },
        { label: "Tipo", render: (item) => item.type ?? "-" },
        { label: "Natureza", render: (item) => item.operationNature ?? "-" },
        {
          label: "Categoria Opex",
          render: (item) => item.opexCategory?.name ?? "-",
        },
        { label: "Ativo", render: (item) => (item.active ? "Sim" : "Nao") },
      ]}
      formFields={[
        { name: "code", label: "Codigo", type: "text", required: true },
        { name: "name", label: "Nome", type: "text", required: true },
        {
          name: "type",
          label: "Tipo",
          type: "select",
          options: ACCOUNT_TYPES,
          required: true,
        },
        {
          name: "operationNature",
          label: "Natureza",
          type: "select",
          options: NATUREZAS_PLANO_CONTAS,
        },
        {
          name: "opexCategoryId",
          label: "Categoria Opex",
          type: "select",
          options: opexOptions.map((item) => ({ value: item.id, label: item.label })),
        },
        { name: "active", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        code: "",
        name: "",
        type: ACCOUNT_TYPES[0]?.value ?? "GROUP",
        operationNature: "OPEX",
        opexCategoryId: null,
        active: true,
      }}
      mapToPayload={(values) => ({
        code: values.code,
        name: values.name,
        type: values.type,
        operationNature: values.operationNature || null,
        opexCategoryId: values.opexCategoryId ? Number(values.opexCategoryId) : null,
        active: values.active,
      })}
      renderHeaderActions={({ query }) => (
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={() => handleExport(query)}
          disabled={exporting}
        >
          {exporting ? "Exportando..." : "Exportar Excel"}
        </button>
      )}
      permissionWrite="ACCOUNT_WRITE"
    />
  );
};
